// Session Summary Page — Simplified Phase 4
// Shows session completion status and navigation options

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useSessions } from '../../hooks/useSessions';
import Button from '../ui/Button';
import Layout from '../layout/Layout';
import { formatDateWithTime, formatDuration } from '../../utils/dateFormatters';

const SessionSummaryPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('session');
  const { user } = useAuth();
  const { getSessionById, loading: sessionsLoading } = useSessions();

  const [session, setSession] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showChatHistory, setShowChatHistory] = useState(false);

  const fetchSession = useCallback(async () => {
    if (!sessionId || !user?.uid) {
      setError(t('page.notFound'));
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await getSessionById(sessionId);
      if (data) {
        setSession(data as unknown as Record<string, unknown>);
      } else {
        setError(t('page.notFound'));
      }
    } catch {
      setError(t('common:errors.fetch.session'));
    } finally {
      setLoading(false);
    }
  }, [sessionId, user?.uid, getSessionById, t]);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  if (loading || sessionsLoading) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
            <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !session) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-8 text-center">
          <span className="text-3xl mb-4 block">😔</span>
          <p className="text-neutral-medium mb-4">{error || t('page.notFound')}</p>
          <Button variant="outline" onClick={() => navigate('/sessions')}>
            {t('summary.backToTeaching')}
          </Button>
        </div>
      </Layout>
    );
  }

  const topicTitle = (session.topicTitle as string) || t('page.defaultTopic');
  const teachingForm = (session.teachingForm as string) || '';
  const durationMinutes = session.durationMinutes as number | undefined;

  // Keywords from navigation state (fresh) or session data (fallback)
  const navKeywords = (location.state as { keywords?: string[] } | null)?.keywords;
  const keywords: string[] = navKeywords || (session.keyTakeaways as string[]) || [];

  // Chat history and routing data
  const chatHistory = (session.chatHistory as Array<{
    role: string;
    content: string;
    timestamp: string;
    toolCalls?: Array<{ toolName: string }>;
  }>) || [];
  const sessionJourneyId = (session.learningJourneyId as string) || (session.journeyId as string) || '';
  const sessionTopicId = (session.topicId as string) || '';

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Session completed */}
        <div className="bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-xl border border-primary-200 dark:border-primary-800 p-6 mb-6">
          <div className="flex items-center gap-4">
            <span className="text-4xl">✅</span>
            <div>
              <h1 className="text-xl font-display font-bold text-neutral-dark dark:text-white">
                {t('summary.sessionCompleted')}
              </h1>
              <p className="text-neutral-medium">{t('summary.wellDone')}</p>
            </div>
          </div>
        </div>

        {/* Session details */}
        <div className="bg-canvas dark:bg-canvas-dark rounded-xl shadow-lg p-6 mb-6 border border-frame-border dark:border-frame-border-dark">
          <h2 className="text-lg font-display font-semibold text-neutral-dark dark:text-white mb-4">
            {topicTitle}
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
              <div className="text-xs text-neutral-medium uppercase tracking-wide mb-1">{t('summary.dateLabel')}</div>
              <div className="text-sm font-medium text-neutral-dark dark:text-white">
                {formatDateWithTime(session.startedAt as string | Date)}
              </div>
            </div>
            {durationMinutes && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                <div className="text-xs text-neutral-medium uppercase tracking-wide mb-1">{t('summary.durationLabel')}</div>
                <div className="text-sm font-medium text-neutral-dark dark:text-white">
                  {formatDuration(durationMinutes)}
                </div>
              </div>
            )}
            {teachingForm && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                <div className="text-xs text-neutral-medium uppercase tracking-wide mb-1">{t('summary.formLabel')}</div>
                <div className="text-sm font-medium text-neutral-dark dark:text-white capitalize">
                  {teachingForm}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Keywords from session */}
        {keywords.length > 0 && (
          <div className="bg-canvas dark:bg-canvas-dark rounded-xl shadow-lg p-6 mb-6 border border-frame-border dark:border-frame-border-dark">
            <h3 className="text-sm font-semibold text-neutral-medium uppercase tracking-wide mb-3">
              {t('summary.keywordsLabel', 'Stikord fra sessionen')}
            </h3>
            <div className="flex flex-wrap gap-2">
              {keywords.map((keyword, i) => (
                <span
                  key={i}
                  className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-700"
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Chat History */}
        {chatHistory.length > 0 && (
          <div className="mt-6">
            <button
              onClick={() => setShowChatHistory(!showChatHistory)}
              className="text-sm text-primary-500 hover:text-primary-400 transition-colors flex items-center gap-1"
            >
              <span>{showChatHistory ? '▾' : '▸'}</span>
              {showChatHistory ? t('summary.chatHistory.hide') : t('summary.chatHistory.show')}
              <span className="text-gray-500 ml-1">({chatHistory.length})</span>
            </button>

            {showChatHistory && (
              <div className="mt-3 space-y-2 max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                {chatHistory.map((msg, i) => (
                  <div
                    key={i}
                    className={`text-sm p-3 rounded-lg ${
                      msg.role === 'user'
                        ? 'bg-primary-50 dark:bg-primary-900/20 ml-8'
                        : 'bg-gray-50 dark:bg-gray-800 mr-8'
                    }`}
                  >
                    <div className="text-xs text-gray-500 mb-1 font-medium">
                      {msg.role === 'user' ? 'Du' : 'Anna'}
                    </div>
                    <p className="text-neutral-dark dark:text-gray-200 whitespace-pre-wrap">
                      {msg.content}
                    </p>
                    {msg.toolCalls && msg.toolCalls.length > 0 && (
                      <div className="mt-1 flex gap-1 flex-wrap">
                        {msg.toolCalls.map((tc, j) => (
                          <span
                            key={j}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                          >
                            🔧 {tc.toolName}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          {sessionJourneyId && sessionTopicId && (
            <Button
              variant="primary"
              onClick={() => navigate(`/workspace/${sessionJourneyId}/${sessionTopicId}`)}
              className="flex-1"
            >
              {t('summary.backToWorkspace')}
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate('/sessions')} className="flex-1">
            {t('summary.backToTeaching')}
          </Button>
          {!!session.journeyId && (
            <Button
              variant="outline"
              onClick={() => navigate(`/preparation/${String(session.journeyId)}${session.topicId ? `/${String(session.topicId)}` : ''}`)}
              className="flex-1"
            >
              📝 {keywords.length > 0
                ? t('summary.goToPreparationWithKeywords', 'Se stikord i Forberedelse')
                : t('common:nav.preparation', 'Forberedelse')
              }
            </Button>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default SessionSummaryPage;
